import { useState, useEffect, useMemo, useRef, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Package,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Tag,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import ConfirmationModal from '../components/ConfirmationModal';
import ManageCategoriesModal from '../components/ManageCategoriesModal';
import ItemFormModal from '../components/ItemFormModal';
import ItemThumbnail from '../components/ItemThumbnail';
import ProductListTotalsBar from '../components/transactions/ProductListTotalsBar';
import { getItems, deleteItem } from '../utils/itemApi';
import { getAllItemCategories } from '../utils/itemCategoryApi';
import {
  encodeItemSort,
  loadProductsTablePreferences,
  parseItemSort,
  saveProductsTablePreferences,
  type SortDir,
} from '../utils/productsTablePreferences';
import type { Item, ItemType } from '../types/item';
import type { PaginationMeta } from '../types/journalEntry';
import type { ItemCategory } from '../types/itemCategory';
import { canDeleteItem, canEditItem } from '../utils/itemForm';
import { formatQuantity } from '../utils/formatCurrency';
import {
  hasOpenBackorders,
  qtyOnHandClassName,
} from '../utils/negativeInventory';
import { toast } from '../utils/toast';
import { computeItemTotals, itemRowKey } from '../utils/itemSelection';

const formatPrice = (amount: string | number) =>
  formatQuantity(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ITEM_TYPES: ItemType[] = ['INVENTORY', 'SERVICE', 'NON_INVENTORY'];
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 400;

type ServerSortKey =
  | 'name'
  | 'sku'
  | 'item_type'
  | 'standard_cost'
  | 'dealer_selling_price'
  | 'cash_price'
  | 'unit_price'
  | 'qty_on_hand';
type ColumnKey = ServerSortKey | 'description' | 'actions';

const SERVER_SORT_KEYS = new Set<ServerSortKey>([
  'name',
  'sku',
  'item_type',
  'standard_cost',
  'dealer_selling_price',
  'cash_price',
  'unit_price',
  'qty_on_hand',
]);

const isServerSortable = (column: ColumnKey | string): column is ServerSortKey =>
  SERVER_SORT_KEYS.has(column as ServerSortKey);

const TABLE_COLUMNS: ColumnKey[] = [
  'name',
  'sku',
  'description',
  'item_type',
  'unit_price',
  'cash_price',
  'dealer_selling_price',
  'standard_cost',
  'qty_on_hand',
  'actions',
];

const MIN_COLUMN_WIDTH = 72;
const CHECKBOX_COLUMN_WIDTH = 36;

const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  name: 240,
  sku: 120,
  description: 200,
  item_type: 140,
  standard_cost: 100,
  dealer_selling_price: 120,
  cash_price: 110,
  unit_price: 100,
  qty_on_hand: 100,
  actions: 80,
};

const defaultSortDir = (key: ServerSortKey): SortDir =>
  key === 'standard_cost' ||
    key === 'dealer_selling_price' ||
    key === 'cash_price' ||
    key === 'unit_price' ||
    key === 'qty_on_hand'
    ? 'desc'
    : 'asc';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getTypeColor = (type: ItemType) => {
  switch (type) {
    case 'INVENTORY':
      return 'bg-slate-100 text-slate-800';
    case 'SERVICE':
      return 'bg-orange-100 text-orange-800';
    case 'NON_INVENTORY':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const Products = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | ItemType>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() => loadProductsTablePreferences().perPage);
  const [itemFormTarget, setItemFormTarget] = useState<Item | 'create' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<ServerSortKey>(() => {
    const parsed = parseItemSort(loadProductsTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.field;
    }
    return 'name';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const parsed = parseItemSort(loadProductsTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.direction;
    }
    return 'asc';
  });
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizeRef = useRef<{ column: ColumnKey; startX: number; startWidth: number } | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const tableMinWidth = useMemo(
    () =>
      CHECKBOX_COLUMN_WIDTH +
      TABLE_COLUMNS.reduce((sum, column) => sum + columnWidths[column], 0),
    [columnWidths]
  );

  useEffect(() => {
    const q = searchParams.get('q')?.trim();
    if (!q) return;
    setSearchInput(q);
    setSearchQuery(q);
  }, []);

  useEffect(() => {
    saveProductsTablePreferences({
      sort: encodeItemSort(sortKey, sortDir),
      perPage,
    });
  }, [sortKey, sortDir, perPage]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!resizeRef.current) return;

      const { column, startX, startWidth } = resizeRef.current;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (event.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [column]: nextWidth }));
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const refreshCategories = async () => {
    const data = await getAllItemCategories({ active: true }).catch(() => [] as ItemCategory[]);
    setCategories(data);
  };

  useEffect(() => {
    const loadCategories = async () => {
      const categoriesData = await getAllItemCategories({ active: true }).catch(() => []);
      setCategories(categoriesData);
    };

    loadCategories();
  }, []);

  const fetchItems = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getItems({
        search: searchQuery || undefined,
        category_id: categoryFilter === 'all' ? undefined : Number(categoryFilter),
        item_type: typeFilter === 'all' ? undefined : typeFilter,
        active:
          statusFilter === 'all' ? undefined : statusFilter === 'active',
        sort: encodeItemSort(sortKey, sortDir),
        per_page: perPage,
        page: pageNum,
      });
      setItems(result.items);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
      setItems([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, typeFilter, categoryFilter, perPage, sortKey, sortDir]);

  const openAddModal = () => {
    setItemFormTarget('create');
  };

  const openEditModal = (item: Item) => {
    setItemFormTarget(item);
  };

  const handleItemSaved = async (item: Item, meta?: { imageError?: string }) => {
    const wasCreate = itemFormTarget === 'create';
    await fetchItems(page);
    await refreshCategories();

    if (meta?.imageError) {
      setItemFormTarget(item);
      setError(meta.imageError);
      return;
    }

    setItemFormTarget(null);
    toast.success(wasCreate ? 'Item created successfully!' : 'Item updated successfully!');
  };

  const handleDeleteItem = (item: Item) => {
    if (!canDeleteItem()) {
      toast.error('Only administrators can delete items.');
      return;
    }

    if (hasOpenBackorders(item)) {
      toast.error('Cannot delete an item with open backorders.');
      return;
    }

    setItemFormTarget(null);
    setDeleteTarget(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await deleteItem(deleteTarget.id);
      setItemFormTarget(null);
      await fetchItems(page);
      toast.success('Item deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const handleRunReport = (item: Item) => {
    navigate('/inventory-transactions', { state: { itemId: item.id } });
  };

  const handleSort = (column: ServerSortKey) => {
    if (sortKey === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir(defaultSortDir(column));
    }
  };

  const startColumnResize = (column: ColumnKey, event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      column,
      startX: event.clientX,
      startWidth: columnWidths[column],
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const renderResizableHeader = (
    label: string,
    column: ColumnKey,
    align: 'left' | 'right' = 'left'
  ) => {
    const active = isServerSortable(column) && sortKey === column;
    const alignClass = align === 'right' ? 'justify-end ml-auto' : '';
    const width = columnWidths[column];

    return (
      <th
        style={{ width, minWidth: width, maxWidth: width }}
        className={`relative px-3 py-3 text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)] ${
          align === 'right' ? 'text-right' : 'text-left'
        }`}
      >
        {column === 'actions' || !isServerSortable(column) ? (
          <span className={`block truncate ${alignClass}`}>{label}</span>
        ) : (
          <button
            type="button"
            onClick={() => handleSort(column)}
            className={`inline-flex max-w-full items-center gap-1 truncate hover:text-gray-800 ${alignClass}`}
          >
            <span className="truncate">{label}</span>
            {active ? (
              sortDir === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5 shrink-0" />
              )
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-30" />
            )}
          </button>
        )}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label} column`}
          onMouseDown={(event) => startColumnResize(column, event)}
          className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-slate-300 active:bg-slate-400"
        />
      </th>
    );
  };

  const cellStyle = (column: ColumnKey): CSSProperties => {
    const width = columnWidths[column];
    return { width, minWidth: width, maxWidth: width };
  };

  const hasActiveFilters =
    searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || categoryFilter !== 'all';

  const sortedCategories = [...categories]
    .filter((c) => c.active)
    .sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name));

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCategoryFilter('all');
    setPage(1);
  };

  const pageTotals = useMemo(() => computeItemTotals(items), [items]);

  const visibleRowKeys = useMemo(() => items.map(itemRowKey), [items]);

  const selectedRows = useMemo(
    () => items.filter((item) => selectedKeys.has(itemRowKey(item))),
    [items, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeItemTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [searchQuery, statusFilter, typeFilter, categoryFilter, page, perPage]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : pageTotals;
  const totalsBarPageSuffix =
    meta && meta.total > pageTotals.count ? ' on this page' : '';
  const showTotalsBar = items.length > 0;

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 lg:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Products</h1>
                <p className="mt-1 text-sm text-gray-500">Inventory, services, and non-inventory items</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowCategoriesModal(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-slate-700 bg-white border border-gray-300 hover:bg-gray-50"
                >
                  <Tag className="h-4 w-4 mr-2" />
                  Manage Categories
                </button>
                {canEditItem() && (
                  <button
                    onClick={openAddModal}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchItems(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-2">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="search"
                      type="text"
                      placeholder="Search by name or SKU..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-slate-800 focus:border-slate-600"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="categoryFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="categoryFilter"
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Categories</option>
                    {sortedCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.fully_qualified_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Item Type
                  </label>
                  <select
                    id="typeFilter"
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value as 'all' | ItemType);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Types</option>
                    {ITEM_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {formatLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <span>Per page</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              {hasActiveFilters && (
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {meta?.total ?? 0} {meta?.total === 1 ? 'item' : 'items'} match
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm text-slate-700 underline"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table
                className="w-full table-fixed border-separate border-spacing-0"
                style={{ minWidth: tableMinWidth }}
              >
                <colgroup>
                  <col style={{ width: CHECKBOX_COLUMN_WIDTH }} />
                  {TABLE_COLUMNS.map((column) => (
                    <col key={column} style={{ width: columnWidths[column] }} />
                  ))}
                </colgroup>
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th
                      style={{
                        width: CHECKBOX_COLUMN_WIDTH,
                        minWidth: CHECKBOX_COLUMN_WIDTH,
                        maxWidth: CHECKBOX_COLUMN_WIDTH,
                      }}
                      className="px-2 py-3 bg-gray-50 sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]"
                    >
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    {renderResizableHeader('Item', 'name')}
                    {renderResizableHeader('SKU', 'sku')}
                    {renderResizableHeader('Description', 'description')}
                    {renderResizableHeader('Type / UoM', 'item_type')}
                    {renderResizableHeader('Retail Price', 'unit_price', 'right')}
                    {renderResizableHeader('Cash Price', 'cash_price', 'right')}
                    {renderResizableHeader('Dealer Price', 'dealer_selling_price', 'right')}
                    {renderResizableHeader('Cost', 'standard_cost', 'right')}
                    {renderResizableHeader('On Hand', 'qty_on_hand', 'right')}
                    {renderResizableHeader('Actions', 'actions', 'right')}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TABLE_COLUMNS.length + 1}
                        className="px-6 py-12 text-center"
                      >
                        <Package className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No items found</p>
                        {canEditItem() && (
                          <button
                            type="button"
                            onClick={openAddModal}
                            className="mt-4 text-sm text-slate-700 underline"
                          >
                            Add your first item
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => {
                      const rowKey = itemRowKey(item);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                      <tr
                        key={item.id}
                        className={`hover:bg-gray-50${
                          isCheckboxSelected ? ' bg-slate-50/60' : ''
                        }`}
                      >
                        <td
                          style={{
                            width: CHECKBOX_COLUMN_WIDTH,
                            minWidth: CHECKBOX_COLUMN_WIDTH,
                            maxWidth: CHECKBOX_COLUMN_WIDTH,
                          }}
                          className="px-2 py-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isCheckboxSelected}
                            onChange={(e) => toggleRow(rowKey, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-slate-800"
                            aria-label={`Select ${item.name}`}
                          />
                        </td>
                        <td style={cellStyle('name')} className="px-3 py-4">
                          <div className="flex items-start gap-3">
                            <ItemThumbnail imageUrl={item.image_url} alt={item.name} size="md" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 break-words">
                                {item.name}
                              </div>
                              {item.category && (
                                <div className="text-xs text-gray-400 truncate" title={item.category.fully_qualified_name}>
                                  {item.category.fully_qualified_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td
                          style={cellStyle('sku')}
                          className="px-3 py-4 text-sm font-mono text-gray-600 truncate"
                          title={item.sku}
                        >
                          {item.sku}
                        </td>
                        <td style={cellStyle('description')} className="px-3 py-4 text-sm text-gray-600 whitespace-pre-wrap break-words">
                          {item.description || '—'}
                        </td>
                        <td style={cellStyle('item_type')} className="px-3 py-4 text-sm overflow-hidden">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(item.item_type)}`}
                          >
                            {formatLabel(item.item_type)}
                          </span>
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {formatLabel(item.unit_of_measure)}
                          </div>
                        </td>
                        <td style={cellStyle('unit_price')} className="px-3 py-4 text-sm text-gray-900 text-right">
                          {formatPrice(item.unit_price)}
                        </td>
                        <td style={cellStyle('cash_price')} className="px-3 py-4 text-sm text-gray-900 text-right">
                          {formatPrice(item.cash_price)}
                        </td>
                        <td
                          style={cellStyle('dealer_selling_price')}
                          className="px-3 py-4 text-sm text-gray-900 text-right"
                        >
                          {formatPrice(item.dealer_selling_price)}
                        </td>
                        <td style={cellStyle('standard_cost')} className="px-3 py-4 text-sm text-gray-900 text-right">
                          {formatPrice(item.standard_cost)}
                        </td>
                        <td style={cellStyle('qty_on_hand')} className="px-3 py-4 text-sm text-right">
                          <span
                            className={
                              item.item_type === 'INVENTORY'
                                ? qtyOnHandClassName(item.qty_on_hand)
                                : 'text-gray-900'
                            }
                          >
                            {formatQuantity(item.qty_on_hand)}
                          </span>
                          {item.item_type === 'INVENTORY' && parseFloat(item.reorder_point) > 0 && (
                            <div className="text-xs text-gray-500">Reorder: {formatQuantity(item.reorder_point)}</div>
                          )}
                        </td>
                        <td style={cellStyle('actions')} className="px-3 py-4 text-right overflow-hidden">
                          <div
                            className="flex justify-end items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => handleRunReport(item)}
                              className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-slate-700 hover:text-slate-900 border border-gray-300 rounded hover:bg-gray-50"
                              title="Run report"
                              aria-label="Run report"
                            >
                              <FileText className="h-3.5 w-3.5" />
                            </button>
                            {canEditItem() && (
                              <button
                                type="button"
                                onClick={() => openEditModal(item)}
                                className="text-slate-600 hover:text-slate-900"
                                title="Edit item"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-sm text-gray-500">
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fetchItems(meta.current_page - 1)}
                  disabled={meta.current_page <= 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => fetchItems(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <ProductListTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              qtyOnHand={totalsBarData.qtyOnHand}
              stockValue={totalsBarData.stockValue}
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>

        {itemFormTarget !== null && (
          <ItemFormModal
            item={itemFormTarget === 'create' ? null : itemFormTarget}
            onClose={() => setItemFormTarget(null)}
            onSaved={handleItemSaved}
            onDeleteRequest={handleDeleteItem}
          />
        )}

        <ConfirmationModal
          isOpen={showDeleteConfirm}
          tone="danger"
          title="Delete item"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.
              </>
            ) : null
          }
          confirmText={deleting ? 'Deleting...' : 'Delete'}
          cancelText="Cancel"
          isConfirming={deleting}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
          }}
          onConfirm={confirmDelete}
        />

      </main>

      {showCategoriesModal && (
        <ManageCategoriesModal
          onClose={async () => {
            setShowCategoriesModal(false);
            await refreshCategories();
          }}
        />
      )}
    </div>
  );
};

export default Products;
